import { createSlice, isAnyOf, PayloadAction } from '@reduxjs/toolkit';
import leftPad from 'left-pad';
import { includes, isEmpty, pluck, prop } from 'ramda';

import ooeConstants from '../constants';
import { logDatadogError } from '../services/datadog';
import type { LocationSummary } from '../types/location';
import { getQueryVariable } from '../util/routerHelpers';
import {
  addUserToAcknowledgement,
  checkEulaMessageOnLoad,
  getEulaMessage,
  getStoreNamesAndNumbers,
  getUserLocations,
  lookupSingleLocation,
} from './thunks';
import { UserInfo } from './types';
import { LocationResult } from './schemas';

export const READ_ONLY_LOCATION_STATUSES = ['closed', 'remodel'] as const;
const VISIBLE_LOCATION_STATUSES = ['open', ...READ_ONLY_LOCATION_STATUSES];

export type State = UserInfo & {
  locations: LocationResult[];
  selectedLocation: string;
  loading: {
    cmtEula: boolean;
    locations: boolean;
  };
  error: {
    locations: Nullable<string>;
    cmtEula: Nullable<string>;
    storeNamesAndNumbers: Nullable<string>;
  };
  bypassBusinessRules: boolean;
  storeNamesAndNumbers: LocationSummary[];
  defaultLocation: string;
  cmtEula: boolean;
  cmtEulaText: string;
};

const initialState: State = {
  name: '',
  type: '',
  email: '',
  cfaAud: '',
  cfaGuid: '',
  permissions: {},
  locations: [],
  selectedLocation: '',
  loading: {
    cmtEula: true,
    locations: true,
  },
  error: {
    locations: null,
    cmtEula: null,
    storeNamesAndNumbers: null,
  },
  bypassBusinessRules: true,
  storeNamesAndNumbers: [],
  defaultLocation: localStorage.getItem(ooeConstants.LOCAL_STORAGE_DEFAULT_LOCATION) || '',
  cmtEula: false,
  cmtEulaText: ooeConstants.EULA_MESSAGE,
};

const slice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setBBRForVCA: (state, action: PayloadAction<{ isVca: boolean }>) => {
      state.bypassBusinessRules = !action.payload.isVca;
    },
    updateUserLocation: (state, action: PayloadAction<{ locationNumber: string }>) => {
      const { locationNumber } = action.payload;
      localStorage.setItem(ooeConstants.LOCAL_STORAGE_DEFAULT_LOCATION, locationNumber);
      state.selectedLocation = locationNumber;
      state.defaultLocation = locationNumber;
      state.error.locations = null;
    },
    logoutUser: () => {},
    toggleBypassBusinessRules: (state) => {
      state.bypassBusinessRules = !state.bypassBusinessRules;
    },
    throwFullScreenError: () => {},
    updateUserInfo: (state, action: PayloadAction<{ user: UserInfo }>) => {
      const { user } = action.payload;
      state.name = user.name;
      state.email = user.email;
      state.cfaAud = user.cfaAud;
      state.cfaGuid = user.cfaGuid;
      state.permissions = user.permissions;
      state.type = user.type;
      state.locationNumbers = user.locationNumbers;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getUserLocations.pending, (state) => {
        state.error.locations = null;
        state.loading.locations = true;
      })
      .addCase(getUserLocations.fulfilled, (state, action) => {
        const { locations } = action.payload;

        state.locations = locations;
        state.error.locations = null;
        state.loading.locations = false;

        const queryLocationNumber = getQueryVariable('locationNumber');
        let foundLocationNumber: string | undefined;
        if (queryLocationNumber) {
          const paddedLocationNumber = leftPad(queryLocationNumber, 5, 0);
          const foundLocation = locations.find(
            ({ locationNumber }) => locationNumber === paddedLocationNumber,
          );
          foundLocationNumber = foundLocation ? prop('locationNumber', foundLocation) : undefined;
        }

        let previouslySelectedValidLocation: string | undefined;
        if (
          state.selectedLocation &&
          locations.find(({ locationNumber }) => locationNumber === state.selectedLocation)
        ) {
          previouslySelectedValidLocation = state.selectedLocation;
        }

        const { defaultLocation } = state;
        const [initialLocation] = locations;
        let initialLocationNumber = initialLocation?.locationNumber;
        if (defaultLocation && includes(defaultLocation, pluck('locationNumber', locations))) {
          initialLocationNumber = defaultLocation;
        }

        const selectedLocation =
          previouslySelectedValidLocation || foundLocationNumber || initialLocationNumber;

        if (defaultLocation !== selectedLocation) {
          localStorage.setItem(ooeConstants.LOCAL_STORAGE_DEFAULT_LOCATION, selectedLocation);
        }

        state.selectedLocation = selectedLocation;
        state.defaultLocation = selectedLocation;
      })
      .addCase(lookupSingleLocation.fulfilled, (state, action) => {
        const { locations } = action.payload;
        state.locations = [...state.locations, ...locations];
        state.selectedLocation = locations.length ? locations[0].locationNumber : '00000';
        state.error.locations = null;
        state.loading.locations = false;
      })
      .addCase(getStoreNamesAndNumbers.fulfilled, (state, action) => {
        const { storeNamesAndNumbers } = action.payload;
        state.storeNamesAndNumbers = storeNamesAndNumbers.filter((store) =>
          VISIBLE_LOCATION_STATUSES.includes(store.status),
        );
      })
      .addCase(getStoreNamesAndNumbers.rejected, (state, action) => {
        const { error } = action;
        logDatadogError('Get Store Names And Numbers Error', { user: state }, error);
        state.error.storeNamesAndNumbers = error.message ?? null;
      })
      .addCase(checkEulaMessageOnLoad.fulfilled, (state, action) => {
        const { acknowledged } = action.payload;
        state.cmtEula = !isEmpty(acknowledged);
        state.loading.cmtEula = false;
      })
      .addCase(checkEulaMessageOnLoad.rejected, (state, action) => {
        const { error } = action;
        logDatadogError('Check Eula Message On Load Error', { user: state }, error);
        state.loading.cmtEula = false;
        state.error.cmtEula = error.message ?? null;
        state.cmtEula = false;
      })
      .addCase(addUserToAcknowledgement.fulfilled, (state) => {
        state.cmtEula = true;
        state.loading.cmtEula = false;
        state.error.cmtEula = null;
      })
      .addCase(addUserToAcknowledgement.rejected, (state, action) => {
        const { error } = action;
        logDatadogError('Add User To Acknowledgement Error', { user: state }, error);
        state.loading.cmtEula = false;
        state.error.cmtEula = error.toString();
        state.cmtEula = false;
      })
      .addCase(getEulaMessage.pending, (state) => {
        state.loading.cmtEula = true;
      })
      .addCase(getEulaMessage.fulfilled, (state, action) => {
        const { text } = action.payload;
        if (text?.content) {
          state.cmtEulaText = text.content;
        }
        state.loading.cmtEula = false;
      })
      .addCase(getEulaMessage.rejected, (state, action) => {
        const { error } = action;
        logDatadogError('Get Eula Message Error', { user: state }, error);
        state.loading.cmtEula = false;
        state.error.cmtEula = error.message ?? null;
      })
      .addMatcher(isAnyOf(getUserLocations.rejected, lookupSingleLocation.rejected), (state, action) => {
        const { error } = action;
        logDatadogError('User Locations Error', { user: state }, error);
        state.error.locations = error.message ?? null;
        state.loading.locations = false;
      });
  },
});

export const actions = slice.actions;
export default slice.reducer;
